import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Link,
  IconButton,
  InputAdornment,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Fab,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Google,
  ArrowForward
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContextNew';
import { authAPI } from '../../services/api';
import CountryCodeSelector from '../../components/CountryCodeSelector';
import './AuthPages.css';

const LoginPage = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [usePhone, setUsePhone] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  
  const { login, enterGuestMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!identifier.trim() || !password.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const loginIdentifier = usePhone ? phoneNumber : identifier;
      
      console.log('🔍 Starting login process for:', loginIdentifier);
      
      // Use the AuthContext login function to properly update state
      const result = await login(loginIdentifier, password);
      
      console.log('🔍 Login result:', result);
      
      if (result.success) {
        console.log('✅ Login successful, checking navigation...');
        
        // Check if there's a return URL to redirect to
        const returnUrl = location.state?.returnUrl;
        console.log('🔍 Return URL:', returnUrl);
        
        if (returnUrl && returnUrl !== '/login' && returnUrl !== '/signup') {
          console.log('🔄 Redirecting to return URL:', returnUrl);
          // Redirect back to the original page
          navigate(returnUrl);
        } else {
          // Check if location is set, if not navigate to location check
          const userLocation = localStorage.getItem('userLocation');
          console.log('🔍 User location:', userLocation);
          
          if (!userLocation) {
            console.log('🔄 No location found, navigating to location-check');
            navigate('/location-check');
          } else {
            console.log('🔄 Location found, navigating to home');
            navigate('/home');
          }
        }
      } else {
        console.log('❌ Login failed:', result.error);
        setError(result.error || 'Login failed');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      setError(error.error || error.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestMode = () => {
    enterGuestMode();
    // Check if location is set, if not navigate to location check
    const userLocation = localStorage.getItem('userLocation');
    if (!userLocation) {
      navigate('/location-check');
    } else {
      navigate('/home');
    }
  };

  const handleSignup = () => {
    navigate('/signup');
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A2A66 0%, #1a3a76 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          zIndex: 10,
          width: 120,
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <img
          src="/logo3.png"
          alt="Logo"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>

      {/* Background Shape */}
      <Box
        sx={{
          position: 'absolute',
          top: 40,
          left: 16,
          width: 500,
          height: '60vw',
          maxHeight: 400,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
          transform: 'rotate(-5deg)',
          zIndex: 1
        }}
      />

      <Container
        maxWidth="sm"
        sx={{
          position: 'relative',
          zIndex: 2,
          pt: { xs: 8, md: 12 },
          pb: 4
        }}
      >
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                color: '#00040B',
                mb: 1,
                fontSize: { xs: '1.75rem', md: '2rem' }
              }}
            >
              Sign in to your account
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                fontSize: { xs: '0.9rem', md: '1rem' }
              }}
            >
              Welcome back! Please enter your details
            </Typography>
          </Box>

          {/* Login Form */}
          <Box component="form" onSubmit={handleLogin} sx={{ mb: 3 }}>
            {/* Email/Phone Toggle */}
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={usePhone}
                    onChange={(e) => setUsePhone(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#006AF9',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#006AF9',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: '#006AF9', textDecoration: 'underline' }}>
                    {usePhone ? 'Login with email address' : 'Login with mobile number'}
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>

            {/* Identifier Field */}
            {usePhone ? (
              <CountryCodeSelector
                value={phoneNumber}
                onChange={setPhoneNumber}
                label="Phone Number"
                placeholder="Enter your phone number"
                helperText="Enter your mobile number with country code"
                sx={{ mb: 2 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLogin(e);
                  }
                }}
              />
            ) : (
              <TextField
                fullWidth
                label="Email Address"
                placeholder="Enter your email"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLogin(e);
                  }
                }}
                type="email"
                variant="outlined"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    '&:hover fieldset': {
                      borderColor: '#006AF9',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#006AF9',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: '#006AF9' }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {/* Password Field */}
            <TextField
              fullWidth
              label="Password"
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleLogin(e);
                }
              }}
              variant="outlined"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  '&:hover fieldset': {
                    borderColor: '#006AF9',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#006AF9',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="span"
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: '#006AF9',
                        display: 'inline-block',
                        mr: 1
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#666' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Error Message */}
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* Forgot Password */}
            <Box sx={{ textAlign: 'right', mb: 3 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleForgotPassword}
                sx={{
                  color: '#006AF9',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Forgot Password?
              </Link>
            </Box>

            {/* Login Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                backgroundColor: '#006AF9',
                background: 'linear-gradient(135deg, #006AF9 0%, #0056CC 100%)',
                boxShadow: '0 4px 15px rgba(0, 106, 249, 0.3)',
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0056CC 0%, #004499 100%)',
                  boxShadow: '0 6px 20px rgba(0, 106, 249, 0.4)',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  boxShadow: 'none',
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Login'
              )}
            </Button>
          </Box>

          {/* Sign Up Link */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#666', display: 'inline' }}>
              Don't have an account?{' '}
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={handleSignup}
              sx={{
                color: '#006AF9',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Sign Up
            </Link>
          </Box>
        </Paper>

        {/* Guest Button - Positioned below form on right side */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          mt: 2,
          pr: { xs: 0, sm: 2 }
        }}>
          <Button
            variant="contained"
            onClick={handleGuestMode}
            sx={{
              background: 'linear-gradient(135deg, #006AF9 0%, #00040B 100%)',
              color: 'white',
              borderRadius: '30px',
              px: 3,
              py: 1.5,
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              '&:hover': {
                background: 'linear-gradient(135deg, #0056CC 0%, #00040B 100%)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
              },
            }}
            startIcon={<ArrowForward sx={{ fontSize: '1rem' }} />}
          >
            Guest
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage; 